import { useState } from "react";
import axios from "axios";
import {
  Shield,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  Info,
  Loader2,
  ChevronDown,
  ChevronUp,
  FileSearch,
  Lightbulb,
  Lock,
  Crown,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Score color helper
const getScoreColor = (score) => {
  if (score >= 80) return "text-green-600";
  if (score >= 60) return "text-amber-500";
  return "text-red-500";
};

const getScoreBackground = (score) => {
  if (score >= 80) return "bg-green-50 border-green-200";
  if (score >= 60) return "bg-amber-50 border-amber-200";
  return "bg-red-50 border-red-200";
};

const getScoreLabel = (score) => {
  if (score >= 80) return "Excelente";
  if (score >= 70) return "Bueno";
  if (score >= 60) return "Aceptable";
  if (score >= 40) return "Necesita mejoras";
  return "Crítico";
};

// Severity icon helper
const SeverityIcon = ({ severity }) => {
  switch (severity) {
    case "critical":
      return <AlertCircle className="w-4 h-4 text-red-500" />;
    case "warning":
      return <AlertTriangle className="w-4 h-4 text-amber-500" />;
    default:
      return <Info className="w-4 h-4 text-blue-500" />;
  }
};

// Category label helper
const getCategoryLabel = (category) => {
  const labels = {
    extractability: "Extracción de texto",
    formatting: "Formato",
    structure: "Estructura",
    content: "Contenido",
    keywords: "Palabras clave",
  };
  return labels[category] || category;
};

export default function ATSAnalysis({ 
  userEmail, 
  userPlan, 
  cvText,
  onAnalysisComplete 
}) {
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [issuesOpen, setIssuesOpen] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);

  const isPro = userPlan === "pro" || userPlan === "business";

  const handleAnalyze = async () => {
    if (!isPro) {
      toast.error("El análisis ATS está disponible solo para usuarios Pro y Business");
      return;
    }

    setAnalyzing(true);
    try {
      const endpoint = cvText 
        ? `${API}/candidate/analyze-ats-text?email=${encodeURIComponent(userEmail)}`
        : `${API}/candidate/analyze-ats?email=${encodeURIComponent(userEmail)}`;
      
      const response = cvText
        ? await axios.post(endpoint, cvText, { headers: { "Content-Type": "text/plain" } })
        : await axios.post(endpoint);
      
      setAnalysis(response.data.analysis);
      if (onAnalysisComplete) {
        onAnalysisComplete(response.data.analysis);
      }
      toast.success("Análisis ATS completado");
    } catch (error) {
      console.error("Error analyzing ATS:", error);
      if (error.response?.status === 403) {
        toast.error("Actualiza a Pro para acceder al análisis ATS");
      } else {
        toast.error(error.response?.data?.detail || "Error al analizar compatibilidad ATS");
      }
    } finally {
      setAnalyzing(false);
    }
  };

  // Not Pro user - show upgrade prompt
  if (!isPro) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-8">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-lg">Análisis ATS Premium</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                Verificá si tu CV es compatible con los sistemas de seguimiento de candidatos (ATS) 
                que usan la mayoría de empresas.
              </p>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm">
              <Crown className="w-4 h-4 text-amber-500" />
              <span className="text-muted-foreground">Disponible en planes Pro y Business</span>
            </div>
            <Button variant="outline" className="gap-2" onClick={() => window.location.href = "/billing"}>
              <Crown className="w-4 h-4" />
              Ver Planes
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Analysis not run yet
  if (!analysis) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Análisis de Compatibilidad ATS
          </CardTitle>
          <CardDescription>
            Verifica si tu CV puede ser leído correctamente por los sistemas de seguimiento de candidatos
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4">
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <FileSearch className="w-4 h-4" />
                ¿Qué es ATS?
              </h4>
              <p className="text-sm text-muted-foreground">
                Los sistemas ATS (Applicant Tracking System) son software que usan las empresas para filtrar CVs automáticamente. 
                Un CV que no sea compatible puede ser descartado antes de que lo vea un reclutador humano.
              </p>
            </div>
            
            <Button 
              onClick={handleAnalyze} 
              disabled={analyzing}
              className="w-full gap-2"
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Analizando...
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4" />
                  Analizar Compatibilidad ATS
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show analysis results
  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary" />
          Análisis de Compatibilidad ATS
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Score Display */}
        <div className={`rounded-xl border p-6 ${getScoreBackground(analysis.score)}`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Puntuación ATS</p>
              <div className="flex items-baseline gap-2 mt-1">
                <span className={`text-4xl font-bold ${getScoreColor(analysis.score)}`}>
                  {analysis.score}
                </span>
                <span className="text-lg text-muted-foreground">/100</span>
              </div>
            </div>
            <div className="text-right">
              <Badge 
                variant={analysis.is_ats_friendly ? "default" : "destructive"}
                className="text-sm px-3 py-1"
              >
                {analysis.is_ats_friendly ? (
                  <><CheckCircle2 className="w-3 h-3 mr-1" /> Compatible ATS</>
                ) : (
                  <><AlertTriangle className="w-3 h-3 mr-1" /> Necesita mejoras</>
                )}
              </Badge>
              <p className={`text-sm mt-2 font-medium ${getScoreColor(analysis.score)}`}>
                {getScoreLabel(analysis.score)}
              </p>
            </div>
          </div>
          <Progress value={analysis.score} className="h-2" />
        </div>

        {/* Breakdown */}
        {analysis.breakdown && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Object.entries(analysis.breakdown).map(([key, value]) => (
              <div key={key} className="text-center p-3 rounded-lg bg-muted/50">
                <div className={`text-xl font-bold ${getScoreColor(value)}`}>{value}%</div>
                <div className="text-xs text-muted-foreground mt-1">{getCategoryLabel(key)}</div>
              </div>
            ))}
          </div>
        )}

        {/* Detailed Feedback */}
        {analysis.detailed_feedback && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <p className="text-sm">{analysis.detailed_feedback}</p>
          </div>
        )}

        {/* Issues */}
        {analysis.issues && analysis.issues.length > 0 && (
          <Collapsible open={issuesOpen} onOpenChange={setIssuesOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between">
                <span className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  Problemas detectados ({analysis.issues.length})
                </span>
                {issuesOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-2 pt-2">
              {analysis.issues.map((issue, idx) => (
                <div 
                  key={idx} 
                  className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border"
                >
                  <SeverityIcon severity={issue.severity} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="text-xs">
                        {getCategoryLabel(issue.category)}
                      </Badge>
                      <Badge 
                        variant={issue.severity === "critical" ? "destructive" : "secondary"}
                        className="text-xs"
                      >
                        {issue.severity === "critical" ? "Crítico" : 
                         issue.severity === "warning" ? "Advertencia" : "Info"}
                      </Badge>
                    </div>
                    <p className="text-sm font-medium">{issue.issue}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      💡 {issue.suggestion}
                    </p>
                  </div>
                </div>
              ))}
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Suggestions */}
        {analysis.suggestions && analysis.suggestions.length > 0 && (
          <Collapsible open={suggestionsOpen} onOpenChange={setSuggestionsOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between">
                <span className="flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-primary" />
                  Sugerencias de mejora ({analysis.suggestions.length})
                </span>
                {suggestionsOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2">
              <ul className="space-y-2">
                {analysis.suggestions.map((suggestion, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                    <span>{suggestion}</span>
                  </li>
                ))}
              </ul>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Re-analyze button */}
        <Button 
          variant="outline" 
          onClick={handleAnalyze} 
          disabled={analyzing}
          className="w-full"
        >
          {analyzing ? (
            <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Analizando...</>
          ) : (
            "Volver a analizar"
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
